import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useDrawer } from "@/context/DrawerContext";
import { TierBadge } from "@/components/TierBadge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, CornerDownLeft } from "lucide-react";

export default function GlobalSearch({ open, setOpen }) {
  const { openLead } = useDrawer();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef();

  useEffect(() => {
    if (!open) { setQ(""); setRes([]); setActive(0); }
    else setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setRes([]); return; }
    const t = setTimeout(() => {
      api.get("/leads", { params: { search: q } })
        .then((r) => { setRes(r.data.slice(0, 8)); setActive(0); })
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (lead) => { setOpen(false); openLead(lead.id); };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, res.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && res[active]) { pick(res[active]); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl rounded-2xl border border-white/10 bg-zinc-950 text-white p-0 gap-0 top-[18%] translate-y-0 shadow-2xl" data-testid="global-search">
        <DialogTitle className="sr-only">Search leads</DialogTitle>
        <div className="flex items-center gap-2.5 px-4 border-b border-white/10">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search leads by name, company, phone, or email…"
            className="flex-1 py-4 text-sm focus:outline-none bg-transparent placeholder:text-zinc-500 text-white"
          />
          <kbd className="text-[10px] font-mono text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto p-1.5">
          {res.map((l, i) => (
            <button
              key={l.id}
              data-testid={`search-result-${l.id}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(l)}
              className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${i === active ? "bg-white/10" : "hover:bg-white/5"}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{l.name}</div>
                <div className="text-xs text-zinc-400 truncate">{l.company} · {l.stage}{l.email ? ` · ${l.email}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-sm font-bold tabular-nums">{l.fit_score}</span>
                <TierBadge tier={l.tier} />
                {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-zinc-500" />}
              </div>
            </button>
          ))}
          {q.trim() && res.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-zinc-500 font-mono">No leads match "{q}"</div>
          )}
          {!q.trim() && (
            <div className="px-4 py-8 text-center text-sm text-zinc-500 font-mono">Type to search across every lead</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
